import { useEffect, useRef, useState } from 'react';

/**
 * Returns `value` after it has stopped changing for `delay` ms.
 *
 * Used for two things: keeping the localStorage write off every keystroke, and
 * holding the score steady while someone is mid-word. Analysis itself is fast
 * enough to run on every render, so this is about not thrashing storage and not
 * flickering the number, not about performance.
 */
export function useDebouncedValue<T>(value: T, delay = 400): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debounced;
}

/**
 * Calls `effect` when `value` settles, skipping the initial mount.
 *
 * The skip matters for autosave: without it, restoring an empty profile on load
 * immediately writes that empty profile back over whatever was stored.
 */
export function useDebouncedEffect<T>(value: T, effect: (value: T) => void, delay = 600): void {
  const isFirstRun = useRef(true);
  const latestEffect = useRef(effect);

  // Updated in an effect rather than during render. Writing to a ref while
  // rendering is unsafe under concurrent rendering, because a render can be
  // thrown away and replayed. Effects run in declaration order, so this lands
  // before the timer below in the same commit.
  useEffect(() => {
    latestEffect.current = effect;
  }, [effect]);

  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    const timer = setTimeout(() => latestEffect.current(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
}
